import { useEffect, useState } from "react";
import { bool, func } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { MinAvgMaxKeys, DescriptionModes} from "@repo/utils";
import { useEstimateEditorStore, useEstimateStructure } from "@tools";
import {
  TextField,
  ButtonSwitch,
  Dialog,
  InfoLabel,
  Alert,
  LevelCheckbox,
} from "@components";
import { Grid, IconButton, Switch } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { DecimalsAndDisplayRounding, BreakdownColumnsList } from "./settings";
import { Settings } from "@assets";

export const BreakdownSettings = observer(({
  noViewLevel,
  noRounding,
  noDescriptionMode,
  externalVisible=false,
  externalOnShow,
}) => {
  
  const { t } = useTranslation();
  const editorStore = useEstimateEditorStore();
  const structure = useEstimateStructure();

  const [ modifierAlertVisible, showModifierAlert ] = useState(externalVisible);
  const [ visible, show ] = useState(externalVisible);

  const {
    viewLevel,
    valueLevel,
    timeModifier,
    useMinMax,
    descriptionMode,
    dayModifier,
    showBreakdownParentValues,
    showBreakdownChildCounters,
  } = structure.settings;

  useEffect(() => {
    show(externalVisible);
  }, [externalVisible])
  
  const handleShowDialog = () => {
    show(!visible);
    externalOnShow && externalOnShow(!visible)
  };

  const handleViewLevelChange = (level) => {
    structure.settings.setViewLevel(level);
  };

  const handleValueLevelChange = (level) => {
    structure.settings.setValueLevel(level);
  };

  const handleDescriptionModeChange = (e, n) => {
    structure.settings.setDescriptionMode(n >= 0 ? n : DescriptionModes.COLUMN);
  };

  // const handleSectionFormatChange = (e, n) => {
  //   structure.settings.setSectionFormat(n || SectionFormats.SIMPLE);
  // };

  const handleShowBreakdownParentValues = () => {
    structure.settings.setShowBreakdownParentValues(!showBreakdownParentValues);
  };
  
  const handleShowBreakdownChildCounters = () => {
    structure.settings.setShowBreakdownChildCounters(!showBreakdownChildCounters);
  };
  
  const checkDayModifier = () => {
    if(editorStore.dayModifier && structure.settings.dayModifier !== editorStore.dayModifier)
      structure.settings.setDayModifier(editorStore.dayModifier);
  }

  const handleTimeModifierChange = (modifier) => {
    if(timeModifier === modifier)
      return;
    
    if(modifier === 0 && (
      structure.settings.useMinMax
        ? (structure.totalHours[MinAvgMaxKeys.MIN] || structure.totalHours[MinAvgMaxKeys.MAX])
        : structure.totalHours[MinAvgMaxKeys.AVG]
    )) {
      showModifierAlert(true);
      return;
    }
    
    structure.settings.setModifier(modifier);
    checkDayModifier();
  }
  
  const handleEstimateFormatChange = (format) => {
    structure.settings.setMinMax(Boolean(format))
  };
  
  const setDayModifierChange = () => {
    structure.historyManager.startGroup();
    checkDayModifier();
    structure.alterElementHours(true);
    structure.settings.setModifier(timeModifier ? 0 : 1);
    showModifierAlert(false);
    structure.historyManager.stopGroup();
  }
  
  return (
    <>
      <IconButton
        id="breakdownSettings"
        onClick={handleShowDialog}
        aria-label={t("views.editor.dialogs.settings.title")}
        color="primary"
        className="text-xl"
      >
        {/*{t("views.editor.dialogs.settings.title")}*/}
        <Settings color="primary" />
      </IconButton>
      <Dialog
        open={visible}
        onClose={handleShowDialog}
        width={472}
        title={t("views.editor.dialogs.settings.title")}
      >
        <Grid item container direction="column">
          
          <InfoLabel className="mt-0" label="views.editor.estimate_in" />
          <ButtonSwitch
            value={timeModifier}
            setValue={handleTimeModifierChange}
            values={[
              { value: 1, label: t("times.hour") },
              { value: 0, label: t("times.day") },
            ]}
            fullWidth
            className="mb-4"
            size="small"
          />
          
          <InfoLabel className="mt-0" label="views.editor.estimate_format" />
          <ButtonSwitch
            value={useMinMax ? 1 : 0}
            setValue={handleEstimateFormatChange}
            values={[
              { value: 0, label: t("formats.single") },
              { value: 1, label: t("formats.min_max") },
            ]}
            fullWidth
            className="mb-4"
            size="small"
          />
          
          {
            !noViewLevel && <Grid item container>
              <Grid item container xs={6}>
                <InfoLabel className="mt-0" label="views.editor.dialogs.settings.level" />
                {
                  [0,1,2,3].map((level, index) => (
                    <LevelCheckbox
                      key={`level${level}`}
                      onCheck={handleViewLevelChange}
                      level={level}
                      index={index}
                      checked={viewLevel >= level}
                      // disabled={level > maxViewLevel}
                    />
                  ))
                }
              </Grid>
              <Grid item container xs={6}>
                <InfoLabel className="mt-0" label="views.editor.dialogs.settings.values" />
                {
                  [0,1,2,3].map((level, index) => (
                    <LevelCheckbox
                      key={`level${level}`}
                      onCheck={handleValueLevelChange}
                      level={level}
                      index={index}
                      checked={valueLevel >= level}
                      disabled={level > viewLevel}
                    />
                  ))
                }
              </Grid>
            </Grid> }
          
          {
            !noDescriptionMode &&
            <>
              <InfoLabel
                className={noViewLevel ? "mt-0" : "mt-6"}
                label="views.editor.dialogs.settings.description_mode"
                translate
              />
              <Autocomplete
                id="descriptionModeSelect"
                value={descriptionMode}
                fullWidth
                size="small"
                onChange={handleDescriptionModeChange}
                getOptionSelected={(o, v) => o === v}
                getOptionLabel={(o) => t("description_mode." + o)}
                disableClearable
                options={[3, 2, 1]}
                renderInput={(params) => (
                  <TextField {...params} label="" />
                )}
              />
            </>
          }
          
          <BreakdownColumnsList noDescriptionAdding={noDescriptionMode} />

          {/*{!noMinMax && (*/}
          {/*  <Grid*/}
          {/*    item*/}
          {/*    container*/}
          {/*    justifyContent="space-between"*/}
          {/*    alignItems="center"*/}
          {/*    wrap="nowrap"*/}
          {/*    className="mb-4"*/}
          {/*  >*/}
          {/*    <InfoLabel label="views.editor.min_max" className="mb-0" />*/}
          {/*    <Switch*/}
          {/*      size="medium"*/}
          {/*      color="primary"*/}
          {/*      checked={useMinMax}*/}
          {/*      onChange={handleMinMaxChange}*/}
          {/*      name={*/}
          {/*        useMinMax ? "Revert to fixed values" : "Allow min-max values"*/}
          {/*      }*/}
          {/*    />*/}
          {/*  </Grid>*/}
          {/*)}*/}
          
          {
            !noViewLevel &&
            <Grid item container wrap="nowrap" className="mb-4">
              <InfoLabel label="views.editor.dialogs.settings.parent_estimates" className="mb-0" />
              <Switch
                size="medium"
                color="primary"
                checked={showBreakdownParentValues || false}
                onChange={handleShowBreakdownParentValues}
              />
            </Grid>
          }
          {
            !noViewLevel &&
            <Grid item container wrap="nowrap" className="mb-4">
              <InfoLabel label="views.editor.dialogs.settings.children_counter" className="mb-0" />
              <Switch
                size="medium"
                color="primary"
                checked={showBreakdownChildCounters || false}
                onChange={handleShowBreakdownChildCounters}
              />
            </Grid>
          }

          {
            !noRounding && <DecimalsAndDisplayRounding />
          }
        </Grid>
      </Dialog>
      <Alert
        isOpen={modifierAlertVisible}
        title={t("common.warning")}
        acceptText={t("common.yes")}
        onAccept={setDayModifierChange}
        onCancel={() => showModifierAlert(false)}
      >
        {t("views.editor.estimate_in_alert.text1", { hours: editorStore.dayModifier || dayModifier })}
        <br />
        <br />
        {t("views.editor.estimate_in_alert.text2")}
      </Alert>
    </>
  );
});

BreakdownSettings.propTypes = {
  noDescriptionMode: bool,
  noViewLevel: bool,
  noRounding: bool,
  externalVisible: bool,
  externalOnShow: func,
};